import { useState } from "react";

export default function useTextFormatting(editorRef, onChange) {
  const sizeArray = ["10", "12", "14", "16", "18", "20", "22", "24"];
  const fontFamilyArray = [
    "Arial",
    "Georgia",
    "Times New Roman",
    "Courier New",
    "Verdana",
    "Tahoma",
    "Comic Sans MS",
    "Sans Serif",
  ];
  const [fontSize, setFontSize] = useState("16px");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [activeCommands, setActiveCommands] = useState({
    bold: false,
    italic: false,
    underline: false,
  });

  const syncContent = () => {
    onChange?.(editorRef.current.innerHTML);
  };

  function wrapSelectionInList(listType, symbol = null) {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    let startContainer = selection.anchorNode;
    let parentList = null;
    while (startContainer) {
      if (
        startContainer.nodeName === "UL" ||
        startContainer.nodeName === "OL"
      ) {
        parentList = startContainer;
        break;
      }
      startContainer = startContainer.parentNode;
    }

    if (parentList) {
      const isSameType =
        (listType === "UL" && parentList.nodeName === "UL") ||
        (listType === "OL" && parentList.nodeName === "OL");

      if (isSameType) {
        if (listType === "UL") {
          const currentSymbol = parentList.dataset.symbol;
          if (currentSymbol === symbol) {
            const fragment = document.createDocumentFragment();
            Array.from(parentList.childNodes).forEach((li) => {
              while (li.firstChild) fragment.appendChild(li.firstChild);
            });
            parentList.parentNode.replaceChild(fragment, parentList);
            return;
          } else if (symbol && currentSymbol !== symbol) {
            parentList.classList.add("custom-list");
            Array.from(parentList.children).forEach((li) => {
              li.dataset.symbol = symbol;
              li.classList.add("custom-list-item");
            });
            parentList.dataset.symbol = symbol;
            return;
          }
        }

        if (listType === "OL") {
          const currentStyle = parentList.style.listStyleType;
          if (currentStyle === symbol) {
            const fragment = document.createDocumentFragment();
            Array.from(parentList.childNodes).forEach((li) => {
              while (li.firstChild) fragment.appendChild(li.firstChild);
            });
            parentList.parentNode.replaceChild(fragment, parentList);
            return;
          } else if (parentList.style.listStyleType !== symbol) {
            parentList.style.listStyleType = symbol;
            parentList.classList.add("custom-list");
            return;
          }
        }

        const fragment = document.createDocumentFragment();
        Array.from(parentList.childNodes).forEach((li) => {
          while (li.firstChild) fragment.appendChild(li.firstChild);
        });
        parentList.parentNode.replaceChild(fragment, parentList);
        return;
      }
      const newList = document.createElement(listType);
      while (parentList.firstChild) newList.appendChild(parentList.firstChild);
      parentList.parentNode.replaceChild(newList, parentList);

      Array.from(newList.children).forEach((li) => {
        li.removeAttribute("data-symbol");
        li.classList.remove("custom-list-item");
      });

      if (listType === "UL") {
        newList.style.listStyleType = "disc";
        newList.classList.add("custom-list");
        if (symbol) {
          newList.dataset.symbol = symbol;
          Array.from(newList.children).forEach((li) => {
            li.dataset.symbol = symbol;
            li.classList.add("custom-list-item");
          });
        }
      } else {
        newList.style.listStyleType = symbol || "decimal";
      }

      newList.style.margin = "4px 0";
      newList.style.paddingLeft = "20px";
      return;
    }

    const fragment = range.extractContents();
    const list = document.createElement(listType);

    Array.from(fragment.childNodes).forEach((node) => {
      const li = document.createElement("li");
      if (node.nodeName === "BR") li.innerHTML = "\u200b";
      else li.appendChild(node.cloneNode(true));

      if (symbol && listType === "UL") {
        li.dataset.symbol = symbol;
        li.classList.add("custom-list-item");
        list.classList.add("custom-list");
      }
      if (listType === "OL") {
        list.style.listStyleType = symbol;
        list.classList.add("custom-list");
      }

      list.appendChild(li);
    });

    if (symbol && listType === "UL") {
      list.classList.add("custom-list");
      Array.from(list.children).forEach((li) => {
        li.classList.add("custom-list-item");
        li.dataset.symbol = symbol;
      });
    } else {
      list.style.listStyleType = listType === "UL" ? "disc" : symbol;
    }

    list.style.margin = "4px 0";
    list.style.paddingLeft = "20px";

    range.insertNode(list);

    const newRange = document.createRange();
    newRange.selectNodeContents(list);
    selection.removeAllRanges();
    selection.addRange(newRange);
  }

  const formatText = (command, value = null, symbol = null) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);

    if (command === "fontSize" || command === "fontFamily") {
      const contents = range.extractContents();
      const walker = document.createTreeWalker(
        contents,
        NodeFilter.SHOW_ELEMENT,
        null
      );
      const nodesToUnwrap = [];
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeName === "SPAN") {
          nodesToUnwrap.push(node);
        }
      }
      nodesToUnwrap.forEach((span) => {
        while (span.firstChild) {
          span.parentNode.insertBefore(span.firstChild, span);
        }
        span.parentNode.removeChild(span);
      });

      const span = document.createElement("span");
      if (command === "fontSize") {
        span.style.fontSize = value;
        setFontSize(value);
      } else {
        span.style.fontFamily = value;
        setFontFamily(value);
      }
      span.appendChild(contents);
      range.insertNode(span);

      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
    } else if (command === "insertUnorderedList") {
      wrapSelectionInList("UL", symbol);
    } else if (command === "insertOrderedList") {
      wrapSelectionInList("OL", symbol);
    } else {
      document.execCommand(command, false, null);
      setActiveCommands({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    }

    syncContent();
    editor.focus();
  };

  //   useEffect(() => {
  //     if (editorRef.current && onChange) {
  //       if (editorRef.current.innerHTML !== "") return;
  //       onChange("");
  //     }
  //   }, [editorRef]);

  return {
    fontSize,
    fontFamily,
    activeCommands,
    sizeArray,
    fontFamilyArray,
    formatText,
    syncContent,
  };
}